import type { Court, GameRecord, Player, Session } from '../types';
import { shuffle } from '../utils/helpers';
import { computePerfScore, playerRating } from './scoring';

// ─────────────────────────────────────────────────
//  Court Assignment
//
//  Rules:
//   1. < 3 courts → always random (no rating influence)
//   2. ≥ 3 courts + 8+ games → smart match (snake-draft by perf score)
//   3. Players who sat out most recently get priority (fair rotation)
//   4. Anyone on a 3-game streak is forced to sit one round
// ─────────────────────────────────────────────────

export interface AssignCourtsInput {
  checkedInIds: string[];
  benchedIds?: string[];
  consecutiveGames: Record<string, number>;
  maxCourts: number;
  games: GameRecord[];
  players: Player[];
}

export interface AssignCourtsResult {
  courts: Court[];
  waiting: string[];
}

/** Return the performance score for a player given the current game history */
function perfScore(playerId: string, players: Player[], games: GameRecord[]): number {
  const player = players.find(p => p.id === playerId);
  const base = player ? playerRating(player) : 3.0;

  let wins = 0, gp = 0, pts = 0;
  for (const game of games) {
    for (const court of game.courts) {
      const inA = court.teamA.includes(playerId);
      const inB = court.teamB.includes(playerId);
      if (!inA && !inB) continue;
      if (court.scoreA == null || court.scoreB == null) continue;
      gp++;
      if (inA) { pts += court.scoreA; if (court.scoreA > court.scoreB) wins++; }
      if (inB) { pts += court.scoreB; if (court.scoreB > court.scoreA) wins++; }
    }
  }
  return computePerfScore(playerId, base, { wins, gp, pts });
}

export function assignCourts({
  checkedInIds,
  benchedIds = [],
  consecutiveGames,
  maxCourts,
  games,
  players,
}: AssignCourtsInput): AssignCourtsResult {
  const activeIds = checkedInIds.filter(id => !benchedIds.includes(id));
  const courtsCount = Math.min(Math.floor(activeIds.length / 4), maxCourts);
  const playersOnCourts = courtsCount * 4;

  // Smart match: only kicks in with 3+ courts AND enough game history
  const useSmartMatch = maxCourts >= 3 && games.length >= 8;

  // Forced rest: anyone who has played 3 consecutive rounds sits out
  const mustSit = activeIds.filter(id => (consecutiveGames[id] ?? 0) >= 3);
  const eligible = activeIds.filter(id => (consecutiveGames[id] ?? 0) < 3);

  let active: string[];
  let waiting: string[];

  if (eligible.length >= playersOnCourts) {
    // Prioritise players with the shorter streak (fewest consecutive rounds played)
    const shuffled = shuffle(eligible);
    shuffled.sort((a, b) => (consecutiveGames[a] ?? 0) - (consecutiveGames[b] ?? 0));
    active = shuffled.slice(0, playersOnCourts);
    waiting = [...shuffled.slice(playersOnCourts), ...shuffle(mustSit)];
  } else {
    // Not enough eligible players — pull the least-tired from mustSit
    const needed = playersOnCourts - eligible.length;
    const mustShuffled = shuffle(mustSit);
    active = [...shuffle(eligible), ...mustShuffled.slice(0, needed)];
    waiting = mustShuffled.slice(needed);
  }

  const courts: Court[] = [];

  if (useSmartMatch) {
    // Snake-draft: pair strongest with weakest as partners → balanced teams
    const bySkill = [...active].sort(
      (a, b) => perfScore(b, players, games) - perfScore(a, players, games)
    );
    for (let c = 0; c < courtsCount; c++) {
      const i = c * 2;
      const j = playersOnCourts - 1 - c * 2;
      courts.push({
        teamA: [bySkill[i], bySkill[j]],
        teamB: [bySkill[i + 1], bySkill[j - 1]],
        scoreA: null,
        scoreB: null,
        submitted: false,
      });
    }
  } else {
    // Truly random
    const rand = shuffle([...active]);
    for (let c = 0; c < courtsCount; c++) {
      const i = c * 4;
      courts.push({
        teamA: [rand[i], rand[i + 1]],
        teamB: [rand[i + 2], rand[i + 3]],
        scoreA: null,
        scoreB: null,
        submitted: false,
      });
    }
  }

  return { courts, waiting };
}

// ─────────────────────────────────────────────────
//  Update consecutive-game counters after a round closes
// ─────────────────────────────────────────────────

export function updateConsecutiveGames(
  session: Session
): Record<string, number> {
  const playedThisRound = new Set<string>();
  session.courts.forEach(c => {
    c.teamA.forEach(id => playedThisRound.add(id));
    c.teamB.forEach(id => playedThisRound.add(id));
  });

  const updated = { ...session.consecutiveGames };
  session.checkedIn.forEach(id => {
    if (playedThisRound.has(id)) {
      updated[id] = (updated[id] ?? 0) + 1;
    } else {
      updated[id] = 0;
    }
  });
  return updated;
}
